import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { type EntityData } from '@adornis/baseql/entities/types.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { PartnerRelationship } from '@chancenwerk/db/semantics/PartnerRelationship.js';
import { SemanticRelationship } from '@chancenwerk/db/semantics/SemanticRelationship.js';
import { KooperationsPartnerClass } from '@chancenwerk/master-data-management/partner/entities/KooperationsPartner.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import {
  BusinessAddressPartnerInformation,
  ExternalContactPerson,
} from '@chancenwerk/master-data-management/users/entities/ExternalContactPerson.js';
import { ExternerMitarbeiter } from '@chancenwerk/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { DateTime } from 'luxon';
import { combineLatest, firstValueFrom, from, map, merge, switchMap, type Observable } from 'rxjs';
import { CRMFlags } from '../CRMFlags.js';
import { Adresse } from '../adresse.js';
import type { Gender } from '../enums/enums.js';
import { CRMFilter } from '../filter/CRMFilter.js';

/**
 * @entity CRMActionResponse
 * This is a proxy object for the crm list. It won't get saved into the database
 */
@Entity()
export class CRMActionResponse extends MongoEntity {
  public static override _class = 'CRMActionResponse';
  public static override _collectionName = 'CRMActionResponse';

  public static override get allFields() {
    return selectionSet(() => this, 4);
  }

  static override resolveID(entity: CRMActionResponse) {
    return entity.entityID ?? '';
  }

  @Field(type => String)
  public entityClass: Maybe<string>;

  @Field(type => CRMFlags)
  public flags: Maybe<CRMFlags>;

  @Field(type => String)
  public entityID: Maybe<string>;

  @Field(type => String)
  public salutationInLetter: Maybe<string>;

  @Field(type => String)
  public geschlecht: Maybe<Gender>;

  @Field(type => String)
  public vorname: Maybe<string>;

  @Field(type => String)
  public name: Maybe<string>;

  @Field(type => Adresse, { default: v => v ?? new Adresse({}) })
  public address: Maybe<Adresse>;

  @Field(type => Boolean)
  public customBusinessAddressEnabled: Maybe<boolean>;

  @Field(type => BusinessAddressPartnerInformation, { default: v => v ?? new BusinessAddressPartnerInformation({}) })
  public businessAddress: Maybe<BusinessAddressPartnerInformation>;

  @Field(type => [String], { default: v => v ?? [] })
  public roles: Maybe<string[]>;

  @Field(type => String)
  public email: Maybe<string>;

  @Field(type => String)
  public personForPersonalChristmanSign: Maybe<string>;

  // eslint-disable-next-line max-lines-per-function
  private static _crmActionResponseAggregation(crmFilter: CRMFilter, onlyCount = false, skip?: number, limit?: number) {
    const handleEmployee = getCollectionHandle<Mitarbeiter>(Mitarbeiter._collectionName);
    const handlePartner = getCollectionHandle<Partner>(Partner._collectionName);
    const publishFilterEmployee = from(publishFilter(Mitarbeiter._class));
    const publishFilterPartner = from(publishFilter(Partner._class));

    // For a projection on a custom entity we need to project some fields based on whether the entity is an instance of the partner class or not
    const usePartnerFieldWithFallback = (partnerfield: any, employeeFallback: any) => {
      return {
        $cond: {
          if: {
            $in: ['$_class', Object.values(KooperationsPartnerClass)],
          },
          then: partnerfield,
          else: employeeFallback,
        },
      };
    };

    return combineLatest([publishFilterEmployee, publishFilterPartner]).pipe(
      switchMap(([pfEmployee, pfPartner]) => {
        if (!pfEmployee) throw new Error('No Publishfilter for Employees found');
        if (!pfPartner) throw new Error('No Publishfilter for Partners found');

        const employeeQuery = handleEmployee.watchQuery(
          pfEmployee,
          skip && limit ? { skip, limit, projection: { _id: 1 } } : { projection: { _id: 1 } },
        );
        const partnerQuery = handlePartner.watchQuery(
          pfPartner,
          skip && limit ? { skip, limit, projection: { _id: 1 } } : { projection: { _id: 1 } },
        );
        return merge([employeeQuery, partnerQuery]).pipe(map(() => ({ publishFilterEmployee, publishFilterPartner })));
      }),
      switchMap(async docs => {
        const filter = crmFilter.getMongoFilter();
        const coll = await getRawCollection<EntityData<Mitarbeiter>>(Mitarbeiter._collectionName);
        const agg = await coll
          .aggregate([
            {
              $match: docs.publishFilterEmployee,
            },
            {
              $match: {
                _class: {
                  $not: {
                    $in: [
                      Student._class,
                      Mitarbeiter._class,
                      VerwaltungsMitarbeiter._class,
                      ExternerMitarbeiter._class,
                    ],
                  },
                },
              },
            },
            {
              $lookup: {
                from: Partner._collectionName,
                as: 'businessAddressPartners',
                localField: 'businessAddressPartnerID',
                foreignField: '_id',
              },
            },
            {
              $addFields: {
                businessAddressPartner: {
                  $arrayElemAt: ['$businessAddressPartners', 0],
                },
              },
            },
            {
              $addFields: {
                resolvedBusinessAddress: {
                  $cond: {
                    if: {
                      $eq: ['$customBusinessAddressEnabled', true],
                    },
                    then: {
                      _class: 'BusinessAddressPartnerInformation',
                      address: '$customBusinessAddress',
                    },
                    else: {
                      _class: 'BusinessAddressPartnerInformation',
                      address: '$businessAddressPartner.address',
                      partnerName: '$businessAddressPartner.name',
                      partnerID: '$businessAddressPartner._id',
                    },
                  },
                },
              },
            },
            {
              $lookup: {
                from: SemanticRelationship._collectionName,
                let: { userID: '$_id' },
                pipeline: [
                  {
                    $match: {
                      $expr: {
                        $and: [
                          {
                            $eq: ['$_class', PartnerRelationship._class],
                          },
                          {
                            $eq: ['$subjectID', '$$userID'],
                          },
                        ],
                      },
                    },
                  },
                ],
                as: 'relations',
              },
            },
            {
              $lookup: {
                from: Partner._collectionName,
                let: { objectIDs: '$relations.objectID' },
                pipeline: [
                  {
                    $match: {
                      $expr: {
                        $in: ['$_id', '$$objectIDs'],
                      },
                    },
                  },
                  {
                    $group: {
                      _id: null,
                      objects: { $push: '$$ROOT' },
                    },
                  },
                  {
                    $project: {
                      _id: 0,
                      objects: 1,
                    },
                  },
                ],
                as: 'partners',
              },
            },
            {
              $addFields: {
                positions: {
                  $map: {
                    input: '$relations',
                    as: 'relation',
                    in: '$$relation.position',
                  },
                },
                partners: {
                  $arrayElemAt: ['$partners.objects', 0],
                },
              },
            },

            {
              $unionWith: {
                coll: Partner._collectionName,
                pipeline: [
                  {
                    $match: docs.publishFilterPartner,
                  },
                  {
                    $match:
                      crmFilter.active === true
                        ? {
                            $or: [
                              { endDate: { $exists: false } },
                              { endDate: null },
                              { endDate: undefined },
                              { endDate: { $gt: DateTime.now().toJSDate() } },
                            ],
                          }
                        : filter.active === false
                        ? { endDate: { $lt: DateTime.now().toJSDate() } }
                        : {},
                  },
                ],
              },
            },
            {
              $addFields: {
                addressFullText: {
                  $concat: ['$address.zipCity.zip', '$address.zipCity.city', ', ', '$address.strasse'],
                },
              },
            },
            {
              $match: filter,
            },
            ...(crmFilter.class !== 'Alle'
              ? [
                  {
                    $match: {
                      _class:
                        crmFilter.class === 'Externe Kontaktpersonen'
                          ? { $in: [ExternalContactPerson._class] }
                          : {
                              $in: Object.values(KooperationsPartnerClass),
                            },
                    },
                  },
                ]
              : []),

            ...(onlyCount
              ? [{ $count: 'count' }]
              : [
                  ...(skip ? [{ $skip: skip }] : []),
                  ...(limit ? [{ $limit: limit }] : []),
                  {
                    $project: {
                      flags: 1,
                      _class: CRMActionResponse._class,
                      _id: '$_id',
                      entityID: '$_id',
                      entityClass: usePartnerFieldWithFallback('Partner', '$_class'),
                      salutationInLetter: 1,
                      geschlecht: usePartnerFieldWithFallback('', '$geschlecht'),
                      vorname: usePartnerFieldWithFallback('', '$vorname'),
                      name: usePartnerFieldWithFallback('$name', '$nachname'),
                      address: usePartnerFieldWithFallback('$address', '$adresse'),
                      businessAddress: usePartnerFieldWithFallback(null, '$resolvedBusinessAddress'),
                      customBusinessAddressEnabled: usePartnerFieldWithFallback(false, '$customBusinessAddressEnabled'),
                      roles: usePartnerFieldWithFallback([], '$positions'),
                      email: 1,
                      personForPersonalChristmanSign: { $concat: ['$vorname', ' ', '$nachname'] },
                    },
                  },
                  {
                    $sort: crmFilter.getMongoOptions().sort,
                  },
                ]),
          ])
          .toArray();

        if (onlyCount) {
          return (agg[0] ?? { count: 0 }).count as number;
        }

        return agg;
      }),
    );
  }

  @Subscription(type => [CRMActionResponse])
  public static subscribeCRMActionResponsePaginatedFiltered(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('crmFilter', type => CRMFilter) crmFilter: CRMFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<CRMActionResponse>) =>
      this._crmActionResponseAggregation(crmFilter, false, skip, limit) as Observable<CRMActionResponse[]>;
  }

  @Subscription(type => Int)
  public static subscribeNumberOfCRMActionResponseFiltered(@Arg('crmFilter', type => CRMFilter) crmFilter: CRMFilter) {
    return (gqlFields: void) => {
      return this._crmActionResponseAggregation(crmFilter, true) as Observable<number>;
    };
  }

  private static _removeEmptyCellsFromCSV(csvString: string) {
    const lines = csvString.split('\n');

    const cleanedLines = lines.map(line => {
      const cells = line.split(';');

      const cleanedCells = cells.map(cell => {
        if (cell.trim() === '') {
          return '';
        } else {
          return cell;
        }
      });

      return cleanedCells.join(';');
    });

    const cleanedCSV = cleanedLines.join('\n');

    return cleanedCSV;
  }

  // TODO We should use a better import export for this functionality
  @Query(type => String)
  public static exportCRMActionResponse(@Arg('crmFilter', type => CRMFilter) crmFilter: CRMFilter) {
    return async (gqlFields: void) => {
      const data = await firstValueFrom(
        CRMActionResponse.subscribeCRMActionResponsePaginatedFiltered(0, 0, crmFilter)(CRMActionResponse.allFields),
      );

      return CRMActionResponse._removeEmptyCellsFromCSV(
        `Anrede im Brief;Geschlecht;Vorname;Name Kooperationspartner / Person;Adresse;Dienstadresse;Position;E-Mail;Weihnachtskartenversand personalisiert durch\n` +
          data
            .filter(Boolean)
            .filter(entity => (entity.name?.length ?? 0) > 0)
            .map(
              entity =>
                `${entity.salutationInLetter ?? ''};${entity.geschlecht ?? ''};${entity.vorname ?? ''};${
                  entity.name ?? ''
                };${entity.address?.toString() ?? ''};${
                  entity.businessAddress
                    ? `${entity.businessAddress.partnerName ? `${entity.businessAddress.partnerName},` : ''} ${
                        entity.businessAddress?.address?.toString() ?? ''
                      }`
                    : ''
                };${entity.roles?.map(r => r?.toString() ?? '').join(', ')};${
                  entity.email?.includes('dummy') ? '' : entity.email ?? ''
                };${entity.personForPersonalChristmanSign ?? ''}`,
            )
            .join('\n'),
      );
    };
  }
}
